/**
 * Volume domain object wrapping vtkImageData with utility methods.
 */

import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';

import type { Vec3, Bounds6 } from './types';

/**
 * Interface for a Volume domain object.
 */
export interface IVolume {
    /** Lookup key used by client */
    readonly key: string;
    /** Original full-resolution image data for 2D slicing */
    readonly imageData: vtkImageData;

    // Computed properties
    readonly center: Vec3;
    readonly bounds: Bounds6;
    readonly dimensions: Vec3;
    readonly spacing: Vec3;
    readonly origin: Vec3;
}

/**
 * Volume domain object wrapping vtkImageData.
 * Provides utility methods for common operations
 */
export class Volume implements IVolume {
    constructor(
        readonly key: string,
        readonly imageData: vtkImageData
    ) {}

    /**
     * Get volume center point in world coordinates.
     */
    get center(): Vec3 {
        const bounds = this.bounds;
        return [(bounds[0] + bounds[1]) / 2, (bounds[2] + bounds[3]) / 2, (bounds[4] + bounds[5]) / 2];
    }

    /**
     * Get volume bounds [xmin, xmax, ymin, ymax, zmin, zmax].
     */
    get bounds(): Bounds6 {
        const b = this.imageData.getBounds();
        return [b[0], b[1], b[2], b[3], b[4], b[5]];
    }

    /**
     * Get volume dimensions [nx, ny, nz] in voxels.
     */
    get dimensions(): Vec3 {
        const d = this.imageData.getDimensions();
        return [d[0], d[1], d[2]];
    }

    /**
     * Get voxel spacing [sx, sy, sz] in mm.
     */
    get spacing(): Vec3 {
        const s = this.imageData.getSpacing();
        return [s[0], s[1], s[2]];
    }

    /**
     * Get volume origin in world coordinates.
     */
    get origin(): Vec3 {
        const o = this.imageData.getOrigin();
        return [o[0], o[1], o[2]];
    }

    /**
     * Convert world coordinates to voxel indices.
     */
    worldToIndex(worldPos: Vec3): Vec3 {
        const indexes = this.imageData.worldToIndexVec3(worldPos);
        return [indexes[0], indexes[1], indexes[2]];
    }

    /**
     * Convert voxel indices to world coordinates.
     */
    indexToWorld(index: Vec3): Vec3 {
        const worldPos = this.imageData.indexToWorldVec3(index);
        return [worldPos[0], worldPos[1], worldPos[2]];
    }

    /**
     * Check if a world position is within the volume bounds.
     */
    containsPoint(worldPos: Vec3): boolean {
        const bounds = this.bounds;
        return (
            worldPos[0] >= bounds[0] &&
            worldPos[0] <= bounds[1] &&
            worldPos[1] >= bounds[2] &&
            worldPos[1] <= bounds[3] &&
            worldPos[2] >= bounds[4] &&
            worldPos[2] <= bounds[5]
        );
    }
}
